/**
 * kp-finishing-addons.js  — KP Finishing v2
 *
 * Mounts on [data-kp-finishing-v2] elements.
 * Fetches the hidden "KP Finishing Services" product via AJAX,
 * renders checkboxes filtered by per-product metafield (custom.finishing_services),
 * intercepts Add to Cart, and adds each selected service as a separate line-item.
 *
 * Shows base price + add-on total in the ATC button badge.
 * If no services are selected in the product metafield, NO finishing options are shown.
 *
 * Zero external dependencies — ES6 IIFE.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ config */

  /** SVG checkmark rendered inside each checkbox pill */
  const CHECK_SVG =
    '<svg aria-hidden="true" focusable="false" width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M1 5L4.5 8.5L11 1" stroke="#7a1f24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  /* --------------------------------------------------------------- utilities */

  /**
   * Format a price in paise as "₹X,XX,XXX" (Indian locale, no decimal).
   * @param {number} paise - price in paise (Shopify's unit)
   */
  function formatINR(paise) {
    const rupees = Math.round(paise / 100);
    return rupees.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    });
  }

  /**
   * Format a delta price as "+₹XXX".
   * @param {number} paise
   */
  function formatDelta(paise) {
    return '+' + formatINR(paise);
  }

  /* ----------------------------------------------------------- core renderer */

  /**
   * Mount a single finishing widget.
   * @param {HTMLElement} root  - the [data-kp-finishing-v2] element
   */
  async function mountWidget(root) {
    const handle     = root.dataset.finishingHandle;
    const productId  = root.dataset.productId;
    const productTitle = root.dataset.productTitle || '';
    const sectionId  = root.dataset.sectionId;
    const formId     = root.dataset.formId;
    const basePricePaise = parseInt(root.dataset.basePrice, 10) || 0;

    if (!handle) return;

    const optionsWrap = root.querySelector('.kp-finish-options');
    if (!optionsWrap) return;

    /* ---------- determine allowed services from metafield ---------- */
    let allowedNames = null;

    const allowedRaw = root.dataset.allowedServices;
    if (typeof allowedRaw === 'string' && allowedRaw.length > 0) {
      try {
        const parsed = JSON.parse(allowedRaw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          allowedNames = parsed;
        } else {
          // Metafield exists but is empty → show nothing
          optionsWrap.innerHTML = '';
          return;
        }
      } catch (e) {
        allowedNames = allowedRaw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        if (!allowedNames.length) {
          optionsWrap.innerHTML = '';
          return;
        }
      }
    } else {
      // No metafield → show nothing (per user requirement)
      optionsWrap.innerHTML = '';
      return;
    }

    /* ---------- fetch finishing product ---------- */
    let variants;
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.json', {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      variants = data.product && data.product.variants;
      if (!variants || !variants.length) throw new Error('No variants found in finishing product.');
    } catch (err) {
      optionsWrap.innerHTML =
        '<p class="kp-finish-error" style="color:#c0392b;font-size:13px;margin:0;">' +
        'Could not load finishing options. ' + err.message + '</p>';
      return;
    }

    /**
     * Extract the canonical service name from a variant title.
     */
    function serviceName(title) {
      var idx = title.indexOf(' / ');
      return idx === -1 ? title : title.substring(0, idx);
    }

    /**
     * Filter out corrupted variants — only keep base/clean ones.
     */
    function isBaseVariant(v) {
      var slash = v.title.indexOf(' / ');
      if (slash === -1) return true;
      return v.title.substring(slash + 3) === 'No finishing';
    }

    const services = variants
      .filter(function (v) {
        if (!isBaseVariant(v)) return false;
        var name = serviceName(v.title);
        if (!allowedNames) return true;
        return allowedNames.indexOf(name) !== -1;
      })
      .map(function (v) {
        return {
          id: v.id,
          name: serviceName(v.title),
          pricePaise: Math.round(parseFloat(v.price) * 100),
        };
      });

    if (!services.length) {
      optionsWrap.innerHTML = '';
      return;
    }

    /* ---------- render ---------- */
    let html = '';

    // "No finishing" pill (checked by default)
    html +=
      '<label class="kp-finish-option kp-finish-option--none kp-finish-option--checked" for="kpf-none-' + sectionId + '">' +
        '<input type="checkbox" id="kpf-none-' + sectionId + '" class="kp-finish-check kp-finish-none" checked value="">' +
        '<span class="kp-finish-box" aria-hidden="true">' + CHECK_SVG + '</span>' +
        '<span class="kp-finish-name">No finishing</span>' +
      '</label>';

    services.forEach(function (svc, i) {
      const uid = 'kpf-' + sectionId + '-' + i;
      html +=
        '<label class="kp-finish-option" for="' + uid + '">' +
          '<input type="checkbox"' +
            ' id="' + uid + '"' +
            ' class="kp-finish-check kp-finish-item"' +
            ' value="' + svc.id + '"' +
            ' data-price="' + svc.pricePaise + '"' +
            ' data-name="' + escAttr(svc.name) + '">' +
          '<span class="kp-finish-box" aria-hidden="true">' + CHECK_SVG + '</span>' +
          '<span class="kp-finish-name">' + escHTML(svc.name) + '</span>' +
          '<span class="kp-finish-delta">' + formatDelta(svc.pricePaise) + '</span>' +
        '</label>';
    });

    // Summary rows: add-ons total + grand total
    html +=
      '<div class="kp-finish-summary" style="display:none;margin-top:8px;font:600 13px/1.6 \'Figtree\',sans-serif;color:#66584e;border-top:1px solid #e8e0d8;padding-top:8px;">' +
        '<div class="kp-finish-addon-row">' +
          'Finishing add-ons: <span class="kp-finish-addon-amount" style="color:#7a1f24;"></span>' +
        '</div>' +
        '<div class="kp-finish-total-row" style="font-size:15px;color:#1a1a1a;margin-top:2px;">' +
          'Total: <span class="kp-finish-grand-total" style="font-weight:700;"></span>' +
        '</div>' +
      '</div>';

    optionsWrap.innerHTML = html;

    /* ---------- interaction ---------- */
    const noneEl    = optionsWrap.querySelector('.kp-finish-none');
    const itemEls   = Array.from(optionsWrap.querySelectorAll('.kp-finish-item'));
    const summaryRow = optionsWrap.querySelector('.kp-finish-summary');
    const addonAmt   = optionsWrap.querySelector('.kp-finish-addon-amount');
    const grandTotal = optionsWrap.querySelector('.kp-finish-grand-total');

    function updateTotal() {
      let addonPaise = 0;
      itemEls.forEach(function (cb) {
        if (cb.checked) addonPaise += parseInt(cb.dataset.price, 10) || 0;
      });

      if (addonPaise > 0) {
        summaryRow.style.display = '';
        addonAmt.textContent = formatDelta(addonPaise);
        grandTotal.textContent = formatINR(basePricePaise + addonPaise);
      } else {
        summaryRow.style.display = 'none';
      }

      // Update ATC button badge with total (base + addons)
      updateAtcBadge(sectionId, basePricePaise, addonPaise);
      return addonPaise;
    }

    function syncCheckedClass(label, checked) {
      label.classList.toggle('kp-finish-option--checked', checked);
    }

    if (noneEl) {
      syncCheckedClass(noneEl.parentElement, true);
      noneEl.addEventListener('change', function () {
        if (noneEl.checked) {
          itemEls.forEach(function (cb) {
            cb.checked = false;
            syncCheckedClass(cb.parentElement, false);
          });
        }
        syncCheckedClass(noneEl.parentElement, noneEl.checked);
        updateTotal();
      });
    }

    itemEls.forEach(function (cb) {
      cb.addEventListener('change', function () {
        if (cb.checked && noneEl) {
          noneEl.checked = false;
          syncCheckedClass(noneEl.parentElement, false);
        }
        syncCheckedClass(cb.parentElement, cb.checked);
        const anyChecked = itemEls.some(function (el) { return el.checked; });
        if (!anyChecked && noneEl) {
          noneEl.checked = true;
          syncCheckedClass(noneEl.parentElement, true);
        }
        updateTotal();
      });
    });

    /* ---------- intercept Add to Cart ---------- */
    const form = document.getElementById(formId);
    if (!form) return;

    if (form.__kpFinishingV2) return;
    form.__kpFinishingV2 = true;

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();

      const baseVariantInput = form.querySelector('[name="id"]');
      const qtyInput = form.querySelector('[name="quantity"]');
      if (!baseVariantInput) return;

      const baseVariantId = baseVariantInput.value;
      const qty = parseInt((qtyInput && qtyInput.value) || '1', 10) || 1;

      const items = [{ id: parseInt(baseVariantId, 10), quantity: qty }];

      const selectedServices = itemEls
        .filter(function (cb) { return cb.checked; })
        .map(function (cb) {
          return {
            variantId: parseInt(cb.value, 10),
            name: cb.dataset.name,
          };
        });

      selectedServices.forEach(function (svc) {
        items.push({
          id: svc.variantId,
          quantity: qty,
          properties: {
            '_kp_parent': productTitle,
            '_kp_finishing': '1',
          },
        });
      });

      const atcBtn = form.querySelector('[type="submit"]');
      const originalText = atcBtn ? atcBtn.innerHTML : '';
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.innerHTML = '<span>Adding…</span>';
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(function () { return {}; });
          throw new Error(errData.description || errData.message || 'Could not add to cart.');
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:update', { bubbles: true, detail: { source: 'kp-finishing-v2' } })
        );

      } catch (err) {
        let errEl = root.querySelector('.kp-finish-submit-error');
        if (!errEl) {
          errEl = document.createElement('p');
          errEl.className = 'kp-finish-submit-error';
          errEl.style.cssText = 'color:#c0392b;font-size:13px;margin:8px 0 0;';
          root.appendChild(errEl);
        }
        errEl.textContent = err.message || 'Something went wrong. Please try again.';
        setTimeout(function () { errEl.textContent = ''; }, 6000);
      } finally {
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.innerHTML = originalText;
        }
      }
    });
  }

  /* --------------------------------------------- ATC button price badge */

  /**
   * Update the [data-kp-atc-price] span with grand total (base + addons).
   * When no addons selected, hide the badge entirely.
   * @param {string} sectionId
   * @param {number} basePaise  - base product price in paise
   * @param {number} addonPaise - total finishing add-on price in paise
   */
  function updateAtcBadge(sectionId, basePaise, addonPaise) {
    const badge = document.querySelector('[data-kp-atc-price="' + sectionId + '"]');
    if (!badge) return;
    if (addonPaise > 0) {
      badge.textContent = ' — ' + formatINR(basePaise + addonPaise);
      badge.style.display = '';
    } else {
      badge.textContent = '';
      badge.style.display = 'none';
    }
  }

  /* ------------------------------------------------ HTML escape helpers */

  function escHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* --------------------------------------------------- boot / init */

  function init() {
    document.querySelectorAll('[data-kp-finishing-v2]').forEach(function (el) {
      mountWidget(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector('[data-kp-finishing-v2]');
    if (root) mountWidget(root);
  });

})();
